import React, { useState, useEffect, useRef } from 'react';
import Lottie from 'lottie-react';
import ReCAPTCHA from 'react-google-recaptcha';
import styled from 'styled-components';
import success from './Lottie/LottieJson/success.json';
import { Title } from '../../Title/Title';
import { Button } from '../../Button/Button';
import { Paragraph } from '../../Paragraph/Paragraph';

interface CFInterface {
  orbit: boolean;
}

const ContactForm: React.FC<CFInterface> = ({ orbit }) => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [recaptchaValidated, setRecaptchaValidated] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const title = 'Contact us';
  const textButton = 'Submit';

  // @ts-ignore
  const Environment = window.OPSEEKER_CONFIG?.ENVIRONMENT ?? 'production';

  useEffect(() => {
    const script = document.createElement('script');
    script.id = 'wf_script';
    script.src = 'https://bigin.zoho.eu/crm/WebformScriptServlet?...';
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleRecaptchaChange = (value: string | null) => {
    setRecaptchaValidated(!!value);
  };

  const removeError = (field: HTMLElement) => {
    const parent = field.closest('.wf-field');
    parent?.classList.remove('wf-field-error-active');
    parent?.querySelector('.wf-field-error')?.remove();
  };

  const setError = (field: HTMLElement, label: string) => {
    const parent = field.closest('.wf-field');
    if (parent && !parent.querySelector('.wf-field-error')) {
      const errorEl = document.createElement('SPAN');
      errorEl.className = 'wf-field-error';
      errorEl.innerHTML = label;
      parent.appendChild(errorEl);
      parent.classList.add('wf-field-error-active');
    }
  };

  const validateFields = (): boolean => {
    // @ts-ignore
    const form = document.forms['BiginWebToRecordForm691033000000504002'] as HTMLFormElement;

    const fields = form.querySelectorAll('[data-fvalidate="true"]') as NodeListOf<HTMLInputElement>;

    let isValid = true;

    fields.forEach((f) => {
      const value = f.value.trim();

      if (!value.match(/^([A-Za-z0-9-._%'+/]+@[A-Za-z0-9.-]+\.[a-zA-Z]{2,22})$/)) {
        setError(f, 'Enter a valid Email');
        isValid = false;
      }
    });

    return isValid;
  };

  const privacyError = (): boolean => {
    const check = document.getElementById('privacycheck691033000000504002') as HTMLInputElement;
    if (check && !check.checked) {
      setError(check, 'Please accept privacy policy and terms');
      return false;
    }
    return true;
  };

  const checkMandatory = (): boolean => {
    let isValid = true;
    // @ts-ignore
    const form = document.forms['BiginWebToRecordForm691033000000504002'];
    const nameField = form['Last Name'] as HTMLInputElement;
    if (!nameField?.value.trim()) {
      setError(nameField, 'Name cannot be empty');
      isValid = false;
    }

    if (!privacyError()) isValid = false;
    if (!validateFields()) isValid = false;

    if (Environment !== 'staging' && Environment !== 'qa' && !recaptchaValidated) {
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (checkMandatory()) {
      // @ts-ignore
      const form = document.forms['BiginWebToRecordForm691033000000504002'];
      try {
        const iframe = document.querySelector<HTMLIFrameElement>(
          'iframe[name="hidden691033000000504002Frame"]'
        );
        await new Promise<void>((resolve) => {
          iframe?.addEventListener('load', () => resolve());
          form.submit();
        });
        setFormSubmitted(true);
      } catch (err) {
        console.error('Form submission failed', err);
      }
    }
  };

  return (
    <>
      {orbit && (
        <Wrapper className='wf-parent'>
          {!formSubmitted ? (
            <FormWrapper
              className='wf-wrapper'
              style={{ padding: '0' }}
              id='BiginWebToRecordFormDiv691033000000504002'
            >
              <Form
                id='BiginWebToRecordForm691033000000504002'
                name='BiginWebToRecordForm691033000000504002'
                method='POST'
                encType='multipart/form-data'
                target='hidden691033000000504002Frame'
                acceptCharset='UTF-8'
                action='https://bigin.zoho.eu/crm/WebForm'
                onSubmit={handleSubmit}
                className='wf-form-component'
              >
                <Title text={title} color='light' />

                {/* Name */}
                <Field className='wf-field wf-field-mandatory'>
                  <input
                    placeholder='Name'
                    name='Last Name'
                    maxLength={80}
                    type='text'
                    onInput={(e) => removeError(e.currentTarget)}
                    id='contact-form-name'
                  />
                </Field>

                {/* Email */}
                <Field className='wf-field'>
                  <input
                    data-fvalidate='true'
                    data-ftype='email'
                    name='Email'
                    placeholder='Email'
                    maxLength={100}
                    type='text'
                    onInput={(e) => removeError(e.target as HTMLInputElement)}
                    id='contact-form-email'
                  />
                </Field>

                {/* Message */}
                <Field className='wf-field'>
                  <textarea
                    placeholder='Message'
                    name='Description'
                    id='contact-form-message'
                    onInput={(e) => removeError(e.currentTarget)}
                  />
                </Field>

                {/* Privacy */}
                <Field className='wf-field'>
                  <CheckField>
                    <label>
                      <input
                        type='checkbox'
                        id='privacycheck691033000000504002'
                        name='privacycheck'
                        onChange={(e) => removeError(e.currentTarget)}
                      />
                      I agree with the{' '}
                      <a
                        href='https://www.oncampusabroad.com/privacy-policy'
                        target='_blank'
                        rel='noopener noreferrer'
                        id='contact-form-privacy-link'
                      >
                        privacy policy
                      </a>{' '}
                      and{' '}
                      <a
                        href='https://www.oncampusabroad.com/legal-notice'
                        target='_blank'
                        rel='noopener noreferrer'
                        id='contact-form-terms-link'
                      >
                        terms and conditions
                      </a>
                    </label>
                  </CheckField>
                </Field>

                {/* Recaptcha */}
                {Environment !== 'staging' && Environment !== 'qa' && (
                  <ReCAPTCHA
                    sitekey='6Lfou_ApAAAAAIpm22ePdsrruIE4CtUtJ0k6SXxf'
                    ref={recaptchaRef}
                    onChange={handleRecaptchaChange}
                  />
                )}

                <Button
                  type='submit'
                  id='formsubmit'
                  textColor='#283288'
                  backgroundColor='#A8E8D3'
                  maxWidth='245px'
                >
                  {textButton}
                </Button>
              </Form>
              <iframe
                title='formFrame'
                name='hidden691033000000504002Frame'
                style={{ display: 'none' }}
              />
            </FormWrapper>
          ) : (
            <SuccessWrapper>
              <Lottie animationData={success} loop={false} style={{ height: 100 }} />
              <Paragraph text='Thank you for your message.' color='light' />
            </SuccessWrapper>
          )}
        </Wrapper>
      )}
    </>
  );
};

export default ContactForm;

const Wrapper = styled.div`
  background-color: #fff;
  font-family: 'League Spartan';
  width: 100%;
  border-radius: 25px;
  background: #283288 !important;
  max-width: 100%;
  padding: 20px;

  #BiginWebToRecordFormDiv69103300000050400 {
    margin: 0 auto !important;
    padding: 0 !important;
  }

  #BiginWebToRecordFormDiv691033000000504002 {
    margin: 0 auto !important;
    padding: 0 !important;
  }

  @media (min-width: 768px) {
    border-radius: 50px;
    width: 470px;
    padding: 40px;
    margin-bottom: 40px;
  }

  @media (min-width: 1024px) {
    border-radius: 50px;
    max-width: 640px;
    min-width: 542.39px;
    padding: 40px;
    position: absolute;
    top: -80px;
  }
`;

const FormWrapper = styled.div`
  max-width: 600px;

  #BiginWebToRecordForm691033000000504002 {
    margin: 0 auto !important;
    padding: 0 !important;
  }
  button {
    text-transform: Capitalize;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 14px;
  padding: 0 !important;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  color: rgba(40, 50, 136, 0.5) !important;

  input,
  textarea {
    padding: 23px 17px;
    border-radius: 8px;
    font-size: 25px;
    color: rgba(40, 50, 136, 0.5) !important;
    font-size: 20px !important;
  }

  input {
    max-height: 45px;
  }

  label {
    color: rgba(40, 50, 136, 0.5);
  }

  a {
    color: rgb(255, 255, 255);
    text-decoration: none;
  }

  .wf-field-error {
    color: red;
    font-size: 16px;
    font-family: 'League Spartan';
  }
`;

const SuccessWrapper = styled.div`
  text-align: center;
  padding: 2rem;
`;

const CheckField = styled.div`
  label {
    color: white;
    font-family: 'League Spartan';
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;

    @media (min-width: 768px;) {
      font-size: 16px;
      line-height: 30px;
    }
  }

  input {
    width: 30px;
    height: 30px;
    margin-right: 10px;
    top: 20px;

    @media (min-width: 768px;) {
      width: 19px;
      height: 19px;
    }
  }
`;
